import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { WorkspaceUserContext } from 'features/globalSetup/contexts/WorkspaceUserContext'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { Schema } from '../../schema'
import { toDTO } from './api/mapper'
import { UPDATE_WORKSPACE_USER } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateWorkspaceUser } from './types'

export const useUpdateWorkspaceUser = (): UseUpdateWorkspaceUser => {
  const { t } = useTranslation('features/globalSetup')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const { setLoading } = useContext(WorkspaceUserContext)

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_WORKSPACE_USER,
    onCompleted: async ({
      updatePublisherByWorkspaceOwner: { errors, user },
    }: Data): Promise<void> => {
      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('general.userManagement.edit.action.error'), { variant: 'error' })
      } else if (user) {
        setErrors({})
        enqueueSnackbar(t('general.userManagement.edit.action.success', { email: user.email }), {
          variant: 'success',
        })
      }
    },
    queriesToInvalidate: ['getWorkspaceUser'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = (data: Schema): void => {
    mutateAsync({
      input: toDTO(data),
    })
  }

  return { errors, update }
}
